import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { collection, doc, getDoc, getDocs, onSnapshot, query, where } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../../firebase";
import { useUserContext } from "../../context/UserProvider";
import {
  addMonthsToMonthKey,
  compareWeekKeys,
  dayKeyToUtcDate,
  formatDayTitle,
  getDerivedStreakState,
  getMonthCalendar,
  getMonthKeyForDate,
  getPreviousWeekKey,
  getWeekKeyForDate,
  parseDayKey,
  resolveTimeZone,
} from "../../features/streaks/streakUtils";
import ManualActivityForm, {
  MANUAL_ACTIVITY_ORIGINS,
} from "../../features/manualActivities/ManualActivityForm";

const DAY_NAMES = ["M", "T", "W", "T", "F", "S", "S"];
const DAY_MS = 24 * 60 * 60 * 1000;
const MAX_STREAK_TREND_WEEKS = 52;

const STREAK_TREND_METRICS = [
  {
    key: "distanceKm",
    label: "Distance",
    accent: "#ff6a00",
  },
  {
    key: "minutes",
    label: "Minutes",
    accent: "#23c25e",
  },
  {
    key: "steps",
    label: "Steps",
    accent: "#14b8a6",
  },
];

function buildStreakWeekNumberMap(summaryData, currentWeekKey) {
  const map = new Map();
  if (!summaryData) {
    return map;
  }
  const lastWeek = summaryData.lastActiveWeekKey;
  const streakCount = getDerivedStreakState(summaryData, currentWeekKey).displayedCurrentStreakWeeks;
  if (!lastWeek || streakCount <= 0) {
    return map;
  }

  let cursor = lastWeek;
  let number = streakCount;
  let guard = 0;
  while (cursor && number > 0 && guard < 200) {
    map.set(cursor, number);
    cursor = getPreviousWeekKey(cursor);
    number -= 1;
    guard += 1;
  }
  return map;
}

function buildCurrentStreakWeekKeys(summaryData, currentWeekKey) {
  const weekKeys = [];
  if (!summaryData) {
    return weekKeys;
  }

  const lastWeek = summaryData.lastActiveWeekKey;
  const streakCount = getDerivedStreakState(summaryData, currentWeekKey).displayedCurrentStreakWeeks;
  if (!lastWeek || streakCount <= 0) {
    return weekKeys;
  }

  let cursor = lastWeek;
  let guard = 0;
  while (cursor && weekKeys.length < streakCount && guard < 200) {
    weekKeys.unshift(cursor);
    cursor = getPreviousWeekKey(cursor);
    guard += 1;
  }

  return weekKeys;
}

function formatGraphMetricValue(metricKey, value) {
  const numericValue = Number(value || 0);

  if (metricKey === "distanceKm") {
    return `${numericValue.toFixed(numericValue >= 10 ? 0 : 1)} km`;
  }
  if (metricKey === "steps") {
    return `${Math.round(numericValue).toLocaleString()} steps`;
  }
  if (metricKey === "minutes") {
    return `${Math.round(numericValue)} min`;
  }
  if (metricKey === "activeDaysCount") {
    const formattedValue = Number.isInteger(numericValue) ? String(numericValue) : numericValue.toFixed(1);
    return `${formattedValue} day${numericValue === 1 ? "" : "s"}`;
  }
  return String(numericValue);
}

function formatTrendWeekLabel(point, fallbackLabel = "") {
  const startDate = dayKeyToUtcDate(point?.weekStartDayKey);
  if (!startDate) {
    return fallbackLabel || point?.weekKey || "";
  }

  return startDate.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

function formatTrendWeekRange(point) {
  const startDate = dayKeyToUtcDate(point?.weekStartDayKey);
  if (!startDate) {
    return point?.weekKey || "";
  }

  const endDate = new Date(startDate.getTime());
  endDate.setUTCDate(endDate.getUTCDate() + 6);
  const startLabel = startDate.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
  const endLabel = endDate.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });

  return `${startLabel} - ${endLabel}`;
}

function formatTrendAxisValue(metricKey, value) {
  const numericValue = Number(value || 0);

  if (metricKey === "steps") {
    if (numericValue >= 1000000) {
      return `${(numericValue / 1000000).toFixed(numericValue >= 10000000 ? 0 : 1)}M`;
    }
    if (numericValue >= 1000) {
      return `${(numericValue / 1000).toFixed(numericValue >= 10000 ? 0 : 1)}k`;
    }
    return String(Math.round(numericValue));
  }

  if (metricKey === "distanceKm") {
    return numericValue.toFixed(numericValue >= 10 ? 0 : 1);
  }

  return String(Math.round(numericValue));
}

function StreakTrendLineGraph({ metric, points, selectedWeekKey, onSelectWeek }) {
  const width = 640;
  const height = 260;
  const padding = {
    top: 42,
    right: 18,
    bottom: 42,
    left: 18,
  };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  const values = points.map((point) => Number(point.value || 0));
  const maxValue = Math.max(1, ...values);
  const yMax = maxValue;
  const yScaleMax = maxValue * 1.12;
  const xForIndex = (index) => {
    if (points.length <= 1) {
      return padding.left + chartWidth / 2;
    }
    return padding.left + (index / (points.length - 1)) * chartWidth;
  };
  const yForValue = (value) => padding.top + chartHeight - (Number(value || 0) / yScaleMax) * chartHeight;
  const coordinates = points.map((point, index) => ({
    ...point,
    x: xForIndex(index),
    y: yForValue(point.value),
  }));
  const linePath = coordinates
    .map((point, index) => `${index === 0 ? "M" : "L"} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`)
    .join(" ");
  const areaPath = coordinates.length
    ? `${linePath} L ${coordinates[coordinates.length - 1].x.toFixed(2)} ${padding.top + chartHeight} L ${coordinates[0].x.toFixed(2)} ${padding.top + chartHeight} Z`
    : "";
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((ratio) => padding.top + chartHeight - ratio * chartHeight);
  const yAxisLabels = [
    {
      label: formatTrendAxisValue(metric.key, yMax),
      y: yForValue(yMax),
    },
    {
      label: formatTrendAxisValue(metric.key, yMax / 2),
      y: yForValue(yMax / 2),
    },
  ];
  const labelIndexes = Array.from(
    new Set(
      [
        0,
        Math.floor((points.length - 1) / 2),
        points.length - 1,
      ].filter((index) => index >= 0)
    )
  );
  const selectedPoint =
    coordinates.find((point) => point.weekKey === selectedWeekKey) ||
    coordinates[coordinates.length - 1] ||
    null;
  const selectedValueLabel = selectedPoint ? formatGraphMetricValue(metric.key, selectedPoint.value) : "";
  const selectedRangeLabel = selectedPoint ? formatTrendWeekRange(selectedPoint) : "";
  const selectedLabelWidth = Math.max(88, selectedValueLabel.length * 6.5 + 16, selectedRangeLabel.length * 5.8 + 16);
  const selectedLabelX = selectedPoint
    ? Math.min(width - selectedLabelWidth / 2, Math.max(selectedLabelWidth / 2, selectedPoint.x))
    : 0;
  const selectedLabelY = selectedPoint ? Math.max(24, selectedPoint.y - 28) : 0;

  if (points.length === 0) {
    return (
      <div
        className="rounded-2xl border px-4 py-4 text-sm"
        style={{
          background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
          borderColor: "rgba(100, 116, 139, 0.2)",
          color: "#64748b",
        }}
      >
        Your streak trend will appear once your streak has at least one completed week.
      </div>
    );
  }

  return (
    <div
        className="rounded-2xl border px-3 py-2 md:px-4 md:py-3 shadow-sm"
        style={{
          backgroundColor: "#ffffff",
          borderColor: "rgba(226, 232, 240, 0.8)",
          boxShadow: "0 6px 18px rgba(15, 23, 42, 0.04)",
      }}
    >
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="block w-full h-auto"
        role="img"
        aria-label={`${metric.label} trend for the current streak`}
      >
        <defs>
          <linearGradient id={`streak-trend-fill-${metric.key}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={metric.accent} stopOpacity="0.14" />
            <stop offset="100%" stopColor={metric.accent} stopOpacity="0.02" />
          </linearGradient>
          <filter id={`streak-trend-glow-${metric.key}`} x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {yTicks.map((tickY) => (
          <line
            key={`trend_y_${tickY}`}
            x1={padding.left}
            x2={padding.left + chartWidth}
            y1={tickY}
            y2={tickY}
            stroke="#edf2f7"
            strokeWidth="1"
          />
        ))}

        {yAxisLabels.map((tick) => (
          <text
            key={`trend_axis_label_${tick.label}_${tick.y}`}
            x={padding.left + 6}
            y={tick.y - 6}
            textAnchor="start"
            fontSize="10.5"
            fontWeight="600"
            fill="#94a3b8"
          >
            {tick.label}
          </text>
        ))}

        {coordinates.map((point, index) => (
          <line
            key={`trend_x_${point.weekKey}`}
            x1={point.x}
            x2={point.x}
            y1={padding.top}
            y2={padding.top + chartHeight}
            stroke={index % 2 === 0 ? "#eef2f7" : "#f8fafc"}
            strokeWidth="1"
            opacity="0.85"
          />
        ))}

        {areaPath && <path d={areaPath} fill={`url(#streak-trend-fill-${metric.key})`} />}
        {linePath && (
          <path
            d={linePath}
            fill="none"
            stroke={metric.accent}
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}

        {coordinates.map((point) => {
          const isSelectedPoint = selectedPoint?.weekKey === point.weekKey;
          return (
            <g key={`trend_point_${point.weekKey}`}>
              {isSelectedPoint && (
                <circle
                  cx={point.x}
                  cy={point.y}
                  r="11"
                  fill={metric.accent}
                  opacity="0.11"
                  filter={`url(#streak-trend-glow-${metric.key})`}
                />
              )}
              <g
                role="button"
                tabIndex="0"
                onClick={() => onSelectWeek(point.weekKey)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onSelectWeek(point.weekKey);
                  }
                }}
                aria-label={`${formatTrendWeekLabel(point, point.weekKey)} ${formatGraphMetricValue(metric.key, point.value)}`}
              >
                <circle
                  cx={point.x}
                  cy={point.y}
                  r={isSelectedPoint ? 4.8 : 4}
                  fill={isSelectedPoint ? metric.accent : "#ffffff"}
                  stroke={metric.accent}
                  strokeWidth="2"
                  style={{ cursor: "pointer" }}
                >
                  <title>
                    {`${formatTrendWeekLabel(point, point.weekKey)}: ${formatGraphMetricValue(metric.key, point.value)}`}
                  </title>
                </circle>
              </g>
            </g>
          );
        })}

        {selectedPoint && (
          <g>
            <line
              x1={selectedPoint.x}
              x2={selectedPoint.x}
              y1={selectedLabelY + 20}
              y2={selectedPoint.y - 8}
              stroke={metric.accent}
              strokeWidth="1.4"
              strokeLinecap="round"
              opacity="0.45"
            />
            <rect
              x={selectedLabelX - selectedLabelWidth / 2}
              y={selectedLabelY - 20}
              width={selectedLabelWidth}
              height="38"
              rx="10"
              fill="#ffffff"
              stroke={metric.accent}
              strokeWidth="1.2"
            />
            <text
              x={selectedLabelX}
              y={selectedLabelY - 5}
              textAnchor="middle"
              fontSize="10"
              fontWeight="600"
              fill="#64748b"
            >
              {selectedRangeLabel}
            </text>
            <text
              x={selectedLabelX}
              y={selectedLabelY + 12}
              textAnchor="middle"
              fontSize="12"
              fontWeight="700"
              fill={metric.accent}
            >
              {selectedValueLabel}
            </text>
          </g>
        )}

        <line
          x1={padding.left}
          x2={padding.left + chartWidth}
          y1={padding.top + chartHeight}
          y2={padding.top + chartHeight}
          stroke="#e2e8f0"
          strokeWidth="1.2"
        />

        {labelIndexes.map((index) => {
          const point = coordinates[index];
          return (
            <text
              key={`trend_label_${point.weekKey}`}
              x={point.x}
              y={height - 12}
              textAnchor={index === 0 ? "start" : index === points.length - 1 ? "end" : "middle"}
              fontSize="11.5"
              fontWeight="600"
              fill="#94a3b8"
            >
              {formatTrendWeekLabel(point)}
            </text>
          );
        })}
      </svg>
    </div>
  );
}

function renderMetricIcon(metricKey, color) {
  const commonProps = {
    width: 16,
    height: 16,
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    "aria-hidden": true,
  };

  if (metricKey === "activeDaysCount") {
    return (
      <svg {...commonProps}>
        <rect x="3.5" y="5.5" width="17" height="15" rx="3" stroke={color} strokeWidth="1.8" />
        <path d="M7.5 3.5V7.5" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
        <path d="M16.5 3.5V7.5" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
        <path d="M3.5 9.5H20.5" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }

  if (metricKey === "distanceKm") {
    return (
      <svg {...commonProps}>
        <circle cx="6" cy="17" r="2.3" stroke={color} strokeWidth="1.8" />
        <circle cx="18" cy="7" r="2.3" stroke={color} strokeWidth="1.8" />
        <path d="M8.5 15.2L15.5 8.8" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M12.8 8.5H15.9V11.6" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  if (metricKey === "minutes") {
    return (
      <svg {...commonProps}>
        <circle cx="12" cy="12" r="8.5" stroke={color} strokeWidth="1.8" />
        <path d="M12 7.8V12L15.2 14.2" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  return (
    <svg {...commonProps}>
      <path
        d="M7.5 17.5C8.3 17.5 9.1 17.1 9.5 16.4L10.6 14.6C10.9 14.1 11.4 13.7 12 13.6L14.6 13.2C15.7 13 16.5 12 16.5 10.9V8.7C16.5 7.8 15.8 7.1 14.9 7.1H12.9C12.4 7.1 11.9 6.8 11.6 6.4L10.6 4.8C10.1 4 9.2 3.5 8.2 3.5H6.3"
        stroke={color}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M5.3 17.5H8.2" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
      <path d="M14.5 18.2H18.3" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
      <path d="M18.3 18.2C19.3 18.2 20.1 17.4 20.1 16.4" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function toTimestampMillis(timestampLike) {
  if (!timestampLike) {
    return 0;
  }
  if (typeof timestampLike?.toMillis === "function") {
    return timestampLike.toMillis();
  }
  if (typeof timestampLike?.seconds === "number") {
    return timestampLike.seconds * 1000;
  }
  if (typeof timestampLike?._seconds === "number") {
    return timestampLike._seconds * 1000;
  }
  return 0;
}

async function waitForStreakSummaryUpdate(summaryRef, previousUpdatedAt, timeoutMs = 8000) {
  const currentSnap = await getDoc(summaryRef);
  const currentUpdatedAt = currentSnap.exists() ? toTimestampMillis(currentSnap.data()?.updatedAt) : 0;
  if (currentUpdatedAt > previousUpdatedAt) {
    return;
  }

  await new Promise((resolve, reject) => {
    let isSettled = false;
    let isInitialSnapshot = true;
    let unsubscribe = () => { };

    const timeoutId = window.setTimeout(() => {
      cleanup();
      reject(new Error("Timed out waiting for streak summary update."));
    }, timeoutMs);

    unsubscribe = onSnapshot(
      summaryRef,
      (snap) => {
        const nextUpdatedAt = snap.exists() ? toTimestampMillis(snap.data()?.updatedAt) : 0;
        if (isInitialSnapshot) {
          isInitialSnapshot = false;
          if (nextUpdatedAt > previousUpdatedAt) {
            cleanup();
            resolve();
          }
          return;
        }

        if (nextUpdatedAt > previousUpdatedAt) {
          cleanup();
          resolve();
        }
      },
      (error) => {
        cleanup();
        reject(error);
      }
    );

    function cleanup() {
      if (isSettled) {
        return;
      }
      isSettled = true;
      window.clearTimeout(timeoutId);
      unsubscribe();
    }
  });
}

function formatEventTime(timestampLike) {
  const millis = toTimestampMillis(timestampLike);
  if (!millis) {
    return "Time unavailable";
  }
  return new Date(millis).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatSource(source) {
  if (!source) {
    return "Unknown";
  }
  return source.charAt(0).toUpperCase() + source.slice(1);
}

function formatEventTitle(event) {
  if (event?.source === "manual" && event?.eventType === "manual") {
    return "Manual activity";
  }
  return `${formatSource(event?.source)} • ${event?.eventType || "activity"}`;
}

export default function StreakPage() {
  const {
    user,
    userData,
    userDetails,
    canStreaks,
    canViewStreaks,
    streakEndDate,
    hasUpcomingStreakRace,
    nextStreakStartDate,
    userRaces,
    userLoading,
  } = useUserContext();
  const [loading, setLoading] = useState(true);
  const [daysByKey, setDaysByKey] = useState({});
  const [weeksByKey, setWeeksByKey] = useState({});
  const [streakSummary, setStreakSummary] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [selectedDayKey, setSelectedDayKey] = useState("");
  const [selectedDayEvents, setSelectedDayEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState("");
  const [deletingEventId, setDeletingEventId] = useState("");
  const [isDayDetailsOpen, setIsDayDetailsOpen] = useState(false);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [isAddActivityOpen, setIsAddActivityOpen] = useState(false);
  const [manualDefaultDate, setManualDefaultDate] = useState("");
  const [manualSubmitError, setManualSubmitError] = useState("");
  const [manualSubmitSuccess, setManualSubmitSuccess] = useState("");
  const [isSubmittingManualActivity, setIsSubmittingManualActivity] = useState(false);
  const [dayNoteDraft, setDayNoteDraft] = useState("");
  const [dayNoteError, setDayNoteError] = useState("");
  const [isEditingDayNote, setIsEditingDayNote] = useState(false);
  const [isSavingDayNote, setIsSavingDayNote] = useState(false);
  const [visibleMonthOffset, setVisibleMonthOffset] = useState(0);
  const [selectedTrendMetricKey, setSelectedTrendMetricKey] = useState("distanceKm");
  const [selectedTrendWeekKey, setSelectedTrendWeekKey] = useState("");
  const [openAverageTooltipMetricKey, setOpenAverageTooltipMetricKey] = useState("");
  const deleteActivityEventCallable = useMemo(
    () => httpsCallable(getFunctions(), "deleteActivityEvent"),
    []
  );
  const saveStreakDayNoteCallable = useMemo(
    () => httpsCallable(getFunctions(), "saveStreakDayNote"),
    []
  );
  const submitManualActivityCallable = useMemo(
    () => httpsCallable(getFunctions(), "submitManualActivity"),
    []
  );
  const manualChallenges = useMemo(
    () => (userRaces instanceof Map ? Array.from(userRaces.values()) : []),
    [userRaces]
  );
  const viewNow = useMemo(() => new Date(), []);
  const streakTimeZone = useMemo(
    () => resolveTimeZone(userData?.streakTimeZone || userData?.timeZone),
    [userData?.streakTimeZone, userData?.timeZone]
  );
  const currentWeekKey = useMemo(
    () => getWeekKeyForDate(viewNow, streakTimeZone),
    [streakTimeZone, viewNow]
  );
  const currentMonthKey = useMemo(
    () => getMonthKeyForDate(viewNow, streakTimeZone),
    [streakTimeZone, viewNow]
  );
  const visibleMonthKey = useMemo(
    () => addMonthsToMonthKey(currentMonthKey, visibleMonthOffset) || currentMonthKey,
    [currentMonthKey, visibleMonthOffset]
  );
  const { monthKey, monthLabel, monthDayKeys, todayKey, weekRows } = useMemo(
    () => getMonthCalendar(visibleMonthKey, streakTimeZone, viewNow),
    [streakTimeZone, viewNow, visibleMonthKey]
  );
  const currentStreakState = useMemo(
    () => getDerivedStreakState(streakSummary, currentWeekKey),
    [currentWeekKey, streakSummary]
  );
  const displayedCurrentStreakWeeks = currentStreakState.displayedCurrentStreakWeeks;
  const previousWeekKey = useMemo(() => getPreviousWeekKey(currentWeekKey), [currentWeekKey]);
  const streakWeekNumberMap = useMemo(
    () => buildStreakWeekNumberMap(streakSummary, currentWeekKey),
    [currentWeekKey, streakSummary]
  );
  const currentStreakWeekKeys = useMemo(
    () => buildCurrentStreakWeekKeys(streakSummary, currentWeekKey),
    [currentWeekKey, streakSummary]
  );
  const isViewingCurrentMonth = monthKey === currentMonthKey;
  const monthWeekKeys = useMemo(
    () =>
      weekRows
        .map((weekRow) => weekRow.find(Boolean))
        .map((dayKey) => {
          if (!dayKey) {
            return null;
          }
          const dayDate = dayKeyToUtcDate(dayKey);
          return dayDate ? getWeekKeyForDate(dayDate, "UTC") : null;
        }),
    [weekRows]
  );
  const showUpcomingRacePreview = !canStreaks && hasUpcomingStreakRace;
  const nextStreakStartLabel = useMemo(() => {
    if (!nextStreakStartDate) {
      return "";
    }
    return new Date(nextStreakStartDate).toLocaleDateString(undefined, {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }, [nextStreakStartDate]);
  const defaultSelectedDayKey = useMemo(() => {
    if (isViewingCurrentMonth && monthDayKeys.includes(todayKey)) {
      return todayKey;
    }
    return monthDayKeys[monthDayKeys.length - 1] || "";
  }, [isViewingCurrentMonth, monthDayKeys, todayKey]);
  const streakTrendWeekKeys = useMemo(
    () => currentStreakWeekKeys.slice(-MAX_STREAK_TREND_WEEKS),
    [currentStreakWeekKeys]
  );
  const availableTrendMetrics = useMemo(
    () =>
      STREAK_TREND_METRICS.filter((metric) =>
        streakTrendWeekKeys.some((weekKey) => Number(weeksByKey[weekKey]?.totals?.[metric.key] || 0) > 0)
      ),
    [streakTrendWeekKeys, weeksByKey]
  );
  const selectedTrendMetric = useMemo(
    () =>
      availableTrendMetrics.find((metric) => metric.key === selectedTrendMetricKey) ||
      availableTrendMetrics[0] ||
      STREAK_TREND_METRICS[0],
    [availableTrendMetrics, selectedTrendMetricKey]
  );
  const streakTrendPoints = useMemo(
    () =>
      streakTrendWeekKeys.map((weekKey) => ({
        weekKey,
        weekStartDayKey: weeksByKey[weekKey]?.weekStartDayKey || "",
        value: Number(weeksByKey[weekKey]?.totals?.[selectedTrendMetric.key] || 0),
      })),
    [selectedTrendMetric.key, streakTrendWeekKeys, weeksByKey]
  );
  const chartMetricDefinitions = useMemo(
    () => [
      {
        key: "activeDaysCount",
        accent: "#ffb900",
        title: "Active days/week",
      },
      {
        key: "distanceKm",
        accent: "#ff6a00",
        title: "Distance/week",
      },
      {
        key: "minutes",
        accent: "#23c25e",
        title: "Minutes/week",
      },
      {
        key: "steps",
        accent: "#14b8a6",
        title: "Steps/week",
      },
    ],
    []
  );
  const chartMetrics = useMemo(() => {
    const streakWeekCount = currentStreakWeekKeys.length;
    const streakAverageTotals = currentStreakWeekKeys.reduce(
      (accumulator, weekKey) => {
        const totals = weeksByKey[weekKey]?.totals || {};
        return {
          activeDaysCount: accumulator.activeDaysCount + Number(totals.activeDaysCount || 0),
          distanceKm: accumulator.distanceKm + Number(totals.distanceKm || 0),
          minutes: accumulator.minutes + Number(totals.minutes || 0),
          steps: accumulator.steps + Number(totals.steps || 0),
        };
      },
      {
        activeDaysCount: 0,
        distanceKm: 0,
        minutes: 0,
        steps: 0,
      }
    );

    const streakAverageValues = {
      activeDaysCount: streakWeekCount > 0 ? streakAverageTotals.activeDaysCount / streakWeekCount : 0,
      distanceKm: streakWeekCount > 0 ? streakAverageTotals.distanceKm / streakWeekCount : 0,
      minutes: streakWeekCount > 0 ? streakAverageTotals.minutes / streakWeekCount : 0,
      steps: streakWeekCount > 0 ? streakAverageTotals.steps / streakWeekCount : 0,
    };

    const availableWeekKeys = Array.from(
      new Set([currentWeekKey, previousWeekKey, ...currentStreakWeekKeys].filter(Boolean))
    );

    return chartMetricDefinitions
      .map((metric) => {
        const currentValue = Number(weeksByKey[currentWeekKey]?.totals?.[metric.key] || 0);
        const previousValue = Number(weeksByKey[previousWeekKey]?.totals?.[metric.key] || 0);
        const averageValue = Number(streakAverageValues[metric.key] || 0);
        const hasData = availableWeekKeys.some(
          (weekKey) => Number(weeksByKey[weekKey]?.totals?.[metric.key] || 0) > 0
        );

        if (!hasData) {
          return null;
        }

        return {
          ...metric,
          bars: [
            { color: metric.accent, label: "This Week", value: currentValue },
            { color: "#64748b", label: "Last Week", value: previousValue },
            { color: "#8a6f6f", label: "Streak Avg", value: averageValue },
          ],
        };
      })
      .filter(Boolean);
  }, [chartMetricDefinitions, currentStreakWeekKeys, currentWeekKey, previousWeekKey, weeksByKey]);

  const loadStreakData = useCallback(async () => {
    if (!user?.uid) {
      setDaysByKey({});
      setWeeksByKey({});
      setStreakSummary(null);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setLoadError("");
      const summaryRef = doc(db, "user_activities", user.uid, "summary", "streak");
      const summarySnap = await getDoc(summaryRef);
      const nextSummary = summarySnap.exists() ? summarySnap.data() : null;
      const streakWeekKeys = buildCurrentStreakWeekKeys(nextSummary, currentWeekKey);
      const dayRefs = monthDayKeys.map((dayKey) => doc(db, "user_activities", user.uid, "days", dayKey));
      const weekRefs = Array.from(
        new Set([currentWeekKey, previousWeekKey, ...monthWeekKeys, ...streakWeekKeys].filter(Boolean))
      ).map((weekKey) => doc(db, "user_activities", user.uid, "weeks", weekKey));

      const [daySnaps, weekSnaps] = await Promise.all([
        Promise.all(dayRefs.map((dayRef) => getDoc(dayRef))),
        Promise.all(weekRefs.map((weekRef) => getDoc(weekRef))),
      ]);

      const nextDaysByKey = {};
      daySnaps.forEach((snap) => {
        if (snap.exists()) {
          nextDaysByKey[snap.id] = snap.data();
        }
      });

      const nextWeeksByKey = {};
      weekSnaps.forEach((snap) => {
        if (snap.exists()) {
          nextWeeksByKey[snap.id] = snap.data();
        }
      });

      setDaysByKey(nextDaysByKey);
      setWeeksByKey(nextWeeksByKey);
      setStreakSummary(nextSummary);
    } catch (err) {
      console.error("Error loading streak page data", err);
      setLoadError("Unable to load streak data right now.");
    } finally {
      setLoading(false);
    }
  }, [currentWeekKey, monthDayKeys, monthWeekKeys, previousWeekKey, user?.uid]);

  const loadEventsForDay = useCallback(async (dayKey) => {
    if (!user?.uid || !dayKey) {
      setSelectedDayEvents([]);
      return;
    }
    try {
      setEventsLoading(true);
      setEventsError("");
      const eventsRef = collection(db, "user_activities", user.uid, "events");
      const q = query(eventsRef, where("dayKey", "==", dayKey));
      const eventsSnap = await getDocs(q);
      const nextEvents = eventsSnap.docs
        .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
        .sort((a, b) => toTimestampMillis(b.startTime) - toTimestampMillis(a.startTime));
      setSelectedDayEvents(nextEvents);
    } catch (err) {
      console.error("Error loading day activities", err);
      setEventsError("Unable to load activities for this day.");
      setSelectedDayEvents([]);
    } finally {
      setEventsLoading(false);
    }
  }, [user?.uid]);

  const refreshDayDoc = useCallback(async (dayKey) => {
    if (!user?.uid || !dayKey) {
      return;
    }

    const dayRef = doc(db, "user_activities", user.uid, "days", dayKey);
    const daySnap = await getDoc(dayRef);
    setDaysByKey((currentDaysByKey) => {
      const nextDaysByKey = { ...currentDaysByKey };
      if (daySnap.exists()) {
        nextDaysByKey[dayKey] = daySnap.data();
      } else {
        delete nextDaysByKey[dayKey];
      }
      return nextDaysByKey;
    });
  }, [user?.uid]);

  useEffect(() => {
    loadStreakData();
  }, [loadStreakData]);

  useEffect(() => {
    if (availableTrendMetrics.length <= 0) {
      return;
    }
    if (!availableTrendMetrics.some((metric) => metric.key === selectedTrendMetricKey)) {
      setSelectedTrendMetricKey(availableTrendMetrics[0].key);
    }
  }, [availableTrendMetrics, selectedTrendMetricKey]);

  useEffect(() => {
    if (streakTrendPoints.length <= 0) {
      if (selectedTrendWeekKey) {
        setSelectedTrendWeekKey("");
      }
      return;
    }

    if (!streakTrendPoints.some((point) => point.weekKey === selectedTrendWeekKey)) {
      setSelectedTrendWeekKey(streakTrendPoints[streakTrendPoints.length - 1].weekKey);
    }
  }, [selectedTrendWeekKey, streakTrendPoints]);

  useEffect(() => {
    if (!selectedDayKey || !selectedDayKey.startsWith(`${monthKey}-`)) {
      setSelectedDayKey(defaultSelectedDayKey);
    }
  }, [defaultSelectedDayKey, monthKey, selectedDayKey]);

  useEffect(() => {
    if (!selectedDayKey) {
      return;
    }
    loadEventsForDay(selectedDayKey);
  }, [loadEventsForDay, selectedDayKey]);

  useEffect(() => {
    if (!isInfoOpen) {
      return;
    }
    function handleEscape(event) {
      if (event.key === "Escape") {
        setIsInfoOpen(false);
      }
    }
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isInfoOpen]);

  useEffect(() => {
    if (!isAddActivityOpen) {
      return;
    }
    function handleEscape(event) {
      if (event.key === "Escape" && !isSubmittingManualActivity) {
        setIsAddActivityOpen(false);
        setManualSubmitError("");
      }
    }
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isAddActivityOpen, isSubmittingManualActivity]);

  useEffect(() => {
    if (!isDayDetailsOpen) {
      return;
    }
    function handleEscape(event) {
      if (event.key === "Escape" && !deletingEventId && !isSavingDayNote) {
        setIsDayDetailsOpen(false);
      }
    }
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [deletingEventId, isDayDetailsOpen, isSavingDayNote]);

  useEffect(() => {
    if (!openAverageTooltipMetricKey) {
      return;
    }
    function handleEscape(event) {
      if (event.key === "Escape") {
        setOpenAverageTooltipMetricKey("");
      }
    }
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [openAverageTooltipMetricKey]);

  function openManualActivityModal() {
    setManualDefaultDate(selectedDayKey && selectedDayKey <= todayKey ? selectedDayKey : todayKey);
    setManualSubmitError("");
    setManualSubmitSuccess("");
    setIsAddActivityOpen(true);
  }

  function closeManualActivityModal() {
    if (isSubmittingManualActivity) {
      return;
    }
    setIsAddActivityOpen(false);
    setManualSubmitError("");
  }

  function openDayDetails(dayKey) {
    setSelectedDayKey(dayKey);
    const dayDate = dayKeyToUtcDate(dayKey);
    const weekKey = dayDate ? getWeekKeyForDate(dayDate, "UTC") : "";
    if (weekKey && streakTrendWeekKeys.includes(weekKey)) {
      setSelectedTrendWeekKey(weekKey);
    }
    setEventsError("");
    setDayNoteDraft(daysByKey[dayKey]?.note || "");
    setDayNoteError("");
    setIsEditingDayNote(false);
    setIsDayDetailsOpen(true);
  }

  async function persistDayNote(nextNoteInput = dayNoteDraft) {
    if (!selectedDayKey || isSavingDayNote) {
      return;
    }

    if (nextNoteInput.trim().length > 1000) {
      setDayNoteError("Notes must be 1000 characters or less.");
      return;
    }

    try {
      setIsSavingDayNote(true);
      setDayNoteError("");
      await saveStreakDayNoteCallable({
        dayKey: selectedDayKey,
        note: nextNoteInput,
      });
      await refreshDayDoc(selectedDayKey);
      setDayNoteDraft(nextNoteInput.trim());
      setIsEditingDayNote(false);
    } catch (err) {
      console.error("Error saving streak day note", err);
      setDayNoteError(err?.message || "Unable to save your note right now. Please try again.");
    } finally {
      setIsSavingDayNote(false);
    }
  }

  async function handleSaveDayNote() {
    await persistDayNote(dayNoteDraft);
  }

  async function handleDeleteDayNote() {
    await persistDayNote("");
  }

  async function handleManualActivitySubmit(activityData) {
    if (isSubmittingManualActivity) {
      return;
    }

    try {
      setIsSubmittingManualActivity(true);
      setManualSubmitError("");
      setManualSubmitSuccess("");
      const previousSummaryUpdatedAt = toTimestampMillis(streakSummary?.updatedAt);
      const summaryRef = doc(db, "user_activities", user.uid, "summary", "streak");
      const response = await submitManualActivityCallable({
        origin: MANUAL_ACTIVITY_ORIGINS.STREAKS_PAGE,
        activityName: activityData.activityName,
        clientSubmissionId: activityData.clientSubmissionId,
        entryMode: activityData.entryMode,
        challengeLegIds: activityData.challengeLegIds,
        date: activityData.date || todayKey,
        minutes: activityData.totalMinutes,
        distanceKm: activityData.distanceKm,
        steps: activityData.steps,
        durationHours: activityData.hours,
        durationMinutes: activityData.minutes,
        durationSeconds: activityData.seconds,
        label: activityData.label || "manual_streak",
      });
      const payload = response?.data || {};

      if (!payload.ledgerSaved) {
        throw new Error("You need active streak access to add manual activities.");
      }

      if (payload.dayKey && monthDayKeys.includes(payload.dayKey)) {
        setSelectedDayKey(payload.dayKey);
        await loadEventsForDay(payload.dayKey);
      } else if (selectedDayKey) {
        await loadEventsForDay(selectedDayKey);
      }

      try {
        await waitForStreakSummaryUpdate(summaryRef, previousSummaryUpdatedAt);
      } catch (waitErr) {
        console.warn("Timed out waiting for streak summary refresh after manual add", waitErr);
      }

      await loadStreakData();
      const challengeCount = Array.isArray(payload.challengeResults) ? payload.challengeResults.length : 0;
      setManualSubmitSuccess(
        challengeCount > 0
          ? `Activity added to your streak and ${challengeCount} challenge${challengeCount === 1 ? "" : "s"}.`
          : "Activity added to your streak ledger."
      );
      setIsAddActivityOpen(false);
    } catch (err) {
      console.error("Error creating manual streak activity", err);
      setManualSubmitError(err?.message || "Unable to add your activity right now. Please try again.");
    } finally {
      setIsSubmittingManualActivity(false);
    }
  }

  async function handleDeleteEvent(activityEvent) {
    const eventId = activityEvent?.id;
    if (!eventId || deletingEventId) {
      return;
    }
    const linkedChallengeLegIds = Array.isArray(activityEvent?.manual?.challengeLegIds)
      ? activityEvent.manual.challengeLegIds.filter(Boolean)
      : [];
    const confirmed = window.confirm(
      linkedChallengeLegIds.length > 0
        ? `Delete this activity from your streak and ${linkedChallengeLegIds.length} linked challenge${linkedChallengeLegIds.length === 1 ? "" : "s"}? All linked totals will be updated.`
        : "Delete this activity from your streak ledger? This updates streak totals but does not change legacy challenge entries."
    );
    if (!confirmed) {
      return;
    }

    try {
      setDeletingEventId(eventId);
      setEventsError("");
      const previousSummaryUpdatedAt = toTimestampMillis(streakSummary?.updatedAt);
      const summaryRef = doc(db, "user_activities", user.uid, "summary", "streak");
      await deleteActivityEventCallable({
        eventId,
        removeLinkedChallenges: linkedChallengeLegIds.length > 0,
      });
      if (selectedDayKey) {
        await loadEventsForDay(selectedDayKey);
      }
      try {
        await waitForStreakSummaryUpdate(summaryRef, previousSummaryUpdatedAt);
      } catch (waitErr) {
        console.warn("Timed out waiting for streak summary refresh after delete", waitErr);
      }
      await loadStreakData();
    } catch (err) {
      console.error("Error deleting activity event", err);
      setEventsError("Unable to delete activity right now. Please try again.");
    } finally {
      setDeletingEventId("");
    }
  }

  if (userLoading || loading) {
    return (
      <div className="py-8 px-3" style={{ background: "#ffffff" }}>
        <div
          className="max-w-4xl mx-auto rounded-3xl border p-6 text-center"
          style={{
            background: "linear-gradient(135deg, #f8fafc 0%, #e8f1fb 100%)",
            borderColor: "rgba(0, 107, 193, 0.16)",
            color: "#64748b",
          }}
        >
          Loading streaks...
        </div>
      </div>
    );
  }

  if (!canViewStreaks) {
    return (
      <div className="py-8 px-3" style={{ background: "#ffffff" }}>
        <div className="max-w-4xl mx-auto">
          <div
            className="rounded-3xl p-6 md:p-8 border text-center"
            style={{
              background: "linear-gradient(135deg, #f8fafc 0%, #e8f1fb 100%)",
              borderColor: "rgba(0, 107, 193, 0.16)",
            }}
          >
            <span className="inline-block text-xs font-semibold tracking-[0.2em] mb-3" style={{ color: "#94a3b8" }}>
              LOCKED
            </span>
            <h2 className="text-2xl font-extrabold mb-2" style={{ color: "#1f2937" }}>
              Streaks
            </h2>
            <p className="mb-5" style={{ color: "#64748b" }}>
              Streaks are available for users with an active challenge or membership.
            </p>
            <Link
              to="/membership"
              className="inline-block px-6 py-3 rounded-xl font-semibold text-white no-underline"
              style={{
                background: "linear-gradient(135deg, #006bc1 0%, #0e89ef 100%)",
                boxShadow: "0 10px 30px rgba(0, 107, 193, 0.25)",
              }}
            >
              Join Membership
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const graceDaysLeft =
    streakEndDate && streakEndDate > Date.now() ? Math.ceil((streakEndDate - Date.now()) / DAY_MS) : 0;
  const selectedDayData = selectedDayKey ? daysByKey[selectedDayKey] : null;
  const activeDaysCount = monthDayKeys.reduce(
    (total, dayKey) => total + (daysByKey[dayKey]?.hasActivity ? 1 : 0),
    0
  );

  return (
    <div className="py-4 md:py-8 px-0 md:px-4" style={{ background: "#ffffff" }}>
      <div className="w-full lg:w-2/3 mx-auto">
        <div
          className="rounded-3xl p-3 md:p-6 border shadow-sm"
          style={{
            background: "linear-gradient(135deg, #f8fafc 0%, #e8f1fb 100%)",
            borderColor: "rgba(0, 107, 193, 0.16)",
          }}
        >
          <div className="mb-3">
            <Link
              to="/"
              className="inline-flex items-center px-3 py-1.5 rounded-xl text-sm font-semibold no-underline border"
              style={{
                color: "#0b5cab",
                borderColor: "rgba(0, 107, 193, 0.24)",
                background: "linear-gradient(135deg, #ffffff 0%, #eff6ff 100%)",
              }}
            >
              Go Home
            </Link>
          </div>

          <div className="flex flex-wrap items-start gap-3 mb-4">
            <div className="flex flex-col items-start">
              <div
                className="rounded-2xl px-4 py-3 min-w-[190px] border"
                style={{
                  background: "linear-gradient(135deg, #ffffff 0%, #eff6ff 100%)",
                  borderColor: "rgba(0, 107, 193, 0.2)",
                }}
              >
                <div className="text-xs uppercase tracking-wide font-semibold" style={{ color: "#64748b" }}>
                  {userDetails?.firstName ? `${userDetails.firstName}'s Current Streak` : "Current Streak"}
                </div>
                <div className="text-2xl font-extrabold leading-tight" style={{ color: "#ff6a00" }}>
                  {displayedCurrentStreakWeeks} weeks
                </div>
                <div className="text-xs mt-1" style={{ color: "#64748b" }}>
                  {showUpcomingRacePreview && nextStreakStartLabel
                    ? `Tracking starts ${nextStreakStartLabel}`
                    : `${activeDaysCount} active day${activeDaysCount === 1 ? "" : "s"} ${isViewingCurrentMonth ? "this month" : `in ${monthLabel}`}`}
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-2 self-center">
              <button
                type="button"
                onClick={() => setIsInfoOpen(true)}
                className="w-7 h-7 rounded-full border text-sm font-bold leading-none flex items-center justify-center"
                style={{
                  borderColor: "rgba(0, 107, 193, 0.35)",
                  color: "#0b5cab",
                  background: "linear-gradient(135deg, #ffffff 0%, #eff6ff 100%)",
                }}
                aria-label="How streaks work"
              >
                ?
              </button>
            </div>
          </div>

          {showUpcomingRacePreview && !!nextStreakStartLabel && (
            <div
              className="mb-4 px-4 py-3 rounded-xl text-sm border"
              style={{
                background: "linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)",
                borderColor: "rgba(2, 132, 199, 0.24)",
                color: "#0c4a6e",
              }}
            >
              Your race starts on {nextStreakStartLabel}. Your activities will
              start to show up here and count towards your streak once the race begins.
            </div>
          )}

          {!!streakEndDate && graceDaysLeft > 0 && (
            <div
              className="mb-4 px-4 py-3 rounded-xl text-sm border"
              style={{
                background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
                borderColor: "rgba(217, 119, 6, 0.25)",
                color: "#92400e",
              }}
            >
              Your streak access ends in {graceDaysLeft} day{graceDaysLeft === 1 ? "" : "s"}.{" "}
              <Link to="/membership" className="font-semibold underline" style={{ color: "#92400e" }}>
                Rejoin here
              </Link>
              .
            </div>
          )}

          {!!loadError && (
            <div
              className="mb-4 text-sm rounded-xl px-3 py-2 border"
              style={{ background: "#fef2f2", color: "#b91c1c", borderColor: "#fecaca" }}
            >
              {loadError}
            </div>
          )}

          {!!manualSubmitSuccess && (
            <div
              className="mb-4 text-sm rounded-xl px-3 py-2 border"
              style={{ background: "#ecfdf5", color: "#166534", borderColor: "#bbf7d0" }}
            >
              {manualSubmitSuccess}
            </div>
          )}

          <div className="mb-4 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => setVisibleMonthOffset((current) => current - 1)}
              className="px-3 py-1.5 rounded-xl border text-sm font-semibold"
              style={{
                borderColor: "rgba(0, 107, 193, 0.24)",
                color: "#0b5cab",
                background: "linear-gradient(135deg, #ffffff 0%, #eff6ff 100%)",
              }}
              aria-label="Show previous month"
            >
              {"< Prev"}
            </button>

            <div className="text-center">
              <div className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: "#94a3b8" }}>
                Calendar
              </div>
              <div className="text-lg md:text-2xl font-extrabold" style={{ color: "#1f2937" }}>
                {monthLabel}
              </div>
            </div>

            <button
              type="button"
              onClick={() => setVisibleMonthOffset((current) => Math.min(0, current + 1))}
              disabled={isViewingCurrentMonth}
              className="px-3 py-1.5 rounded-xl border text-sm font-semibold disabled:opacity-50"
              style={{
                borderColor: "rgba(0, 107, 193, 0.24)",
                color: "#0b5cab",
                background: "linear-gradient(135deg, #ffffff 0%, #eff6ff 100%)",
              }}
              aria-label="Show next month"
            >
              {"Next >"}
            </button>
          </div>

          <div
            className="rounded-2xl p-2 md:p-4 border"
            style={{
              background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
              borderColor: "rgba(100, 116, 139, 0.22)",
            }}
          >
            <div className="grid grid-cols-[repeat(7,minmax(0,1fr))_42px] md:grid-cols-[repeat(7,minmax(0,1fr))_72px] gap-x-1 md:gap-x-2 gap-y-1.5 md:gap-y-2 items-center">
              {DAY_NAMES.map((dayName, idx) => (
                <div
                  key={`day_name_${idx}`}
                  className="text-center text-[11px] md:text-base font-semibold tracking-wide"
                  style={{ color: "#23c25e" }}
                >
                  {dayName}
                </div>
              ))}
              <div className="text-center text-[11px] md:text-xs font-semibold" style={{ color: "#94a3b8" }}>
                Wk
              </div>

              {weekRows.map((weekRow, weekIndex) => (
                <React.Fragment key={`month_row_${monthKey}_${weekIndex}`}>
                  {(() => {
                    const weekKey = monthWeekKeys[weekIndex];
                    const weekData = weekKey ? weeksByKey[weekKey] : null;
                    const weekHasActivity = weekData?.hasActivity === true;
                    const weekComparison = weekKey ? compareWeekKeys(weekKey, currentWeekKey) : null;
                    const isCurrentWeek = weekComparison === 0 && !!weekKey;
                    const isFutureWeek = weekComparison !== null && weekComparison > 0;
                    const isPastWeek = weekComparison !== null && weekComparison < 0;
                    const streakWeekNumber = weekKey ? streakWeekNumberMap.get(weekKey) : null;
                    const weekMinutes = Math.round(Number(weekData?.totals?.minutes || 0));

                    let badgeBackground = "#d1d5db";
                    let badgeText = "";
                    if (isFutureWeek) {
                      badgeBackground = "#e5e7eb";
                    } else if (isCurrentWeek) {
                      badgeBackground = weekHasActivity ? "#ff6a00" : "#93c5fd";
                      badgeText = weekHasActivity && streakWeekNumber ? String(streakWeekNumber) : "";
                    } else if (isPastWeek && weekHasActivity) {
                      badgeBackground = "#ff6a00";
                      badgeText = streakWeekNumber ? String(streakWeekNumber) : "";
                    }

                    return (
                      <>
                        {weekRow.map((dayKey, dayOffset) => {
                          if (!dayKey) {
                            return (
                              <div
                                key={`day_cell_empty_${monthKey}_${weekIndex}_${dayOffset}`}
                                className="h-[54px] sm:h-[58px] md:h-[88px] w-full rounded-lg md:rounded-xl"
                                style={{ background: "rgba(226, 232, 240, 0.28)" }}
                                aria-hidden="true"
                              />
                            );
                          }

                          const dayNumber = parseDayKey(dayKey)?.day;
                          const dayData = daysByKey[dayKey];
                          const hasActivity = dayData?.hasActivity === true;
                          const hasNote = typeof dayData?.note === "string" && dayData.note.trim().length > 0;
                          const minutes = Math.round(Number(dayData?.totals?.minutes || 0));
                          const isToday = dayKey === todayKey;
                          const isFutureDay = dayKey > todayKey;

                          let dayColor = "#6b7280";
                          if (hasActivity) {
                            dayColor = "#23c25e";
                          } else if (isFutureDay || isToday) {
                            dayColor = "#006bc1";
                          }

                          const isSelected = dayKey === selectedDayKey;

                          return (
                            <button
                              type="button"
                              key={`day_cell_${dayKey}`}
                              className="relative h-[54px] sm:h-[58px] md:h-[88px] w-full flex flex-col items-center justify-start rounded-lg md:rounded-xl p-0.5 md:p-2 border transition-colors overflow-hidden"
                              style={{
                                borderColor: isSelected ? "#006bc1" : "transparent",
                                background: isSelected
                                  ? "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)"
                                  : "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                              }}
                              onClick={() => openDayDetails(dayKey)}
                              title={formatDayTitle(dayKey)}
                            >
                              {hasNote && (
                                <span
                                  className="absolute top-1 right-1 md:top-2 md:right-2 inline-flex h-4 w-4 md:h-5 md:w-5 items-center justify-center rounded-full border"
                                  style={{
                                    color: "#0b5cab",
                                    backgroundColor: "#ffffff",
                                    borderColor: "rgba(191, 219, 254, 0.95)",

                                  }}
                                  aria-hidden="true"
                                >
                                  <svg
                                    width="10"
                                    height="10"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M7 17.5H5.5C4.67 17.5 4 16.83 4 16V6.5C4 5.67 4.67 5 5.5 5H18.5C19.33 5 20 5.67 20 6.5V16C20 16.83 19.33 17.5 18.5 17.5H12L8.5 20V17.5H7Z"
                                      stroke="currentColor"
                                      strokeWidth="1.8"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </span>
                              )}

                              <div
                                className={`text-base sm:text-lg md:text-3xl font-semibold leading-none ${isToday ? "w-6 h-6 sm:w-7 sm:h-7 md:w-10 md:h-10 rounded-full flex items-center justify-center" : ""
                                  }`}
                                style={{
                                  color: isToday && hasActivity ? "#ffffff" : dayColor,
                                  border: isToday ? `1px solid ${hasActivity ? "#23c25e" : "#006bc1"}` : "none",
                                  backgroundColor: isToday && hasActivity ? "#23c25e" : "transparent",
                                }}
                              >
                                {dayNumber}
                              </div>

                              {hasActivity && (
                                <span
                                  className="mt-0.5 max-w-[95%] px-1 py-0.5 rounded-full text-white text-[8px] sm:text-[9px] md:text-xs font-semibold leading-none text-center whitespace-nowrap"
                                  style={{ backgroundColor: "#23c25e" }}
                                >
                                  {minutes > 0 ? `${minutes}m` : "✅"}
                                </span>
                              )}
                            </button>
                          );
                        })}
                        <div
                          className="self-stretch w-full rounded-lg md:rounded-xl flex flex-col items-center justify-center gap-0.5 py-1 border"
                          style={{
                            background: weekHasActivity
                              ? "linear-gradient(135deg, #ffedd5 0%, #fed7aa 100%)"
                              : "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
                            borderColor: "rgba(148, 163, 184, 0.25)",
                          }}
                        >
                          <div
                            className="w-7 h-7 md:w-12 md:h-12 rounded-full flex items-center justify-center text-white text-xs md:text-lg font-bold border-2 md:border-4 border-white"
                            style={{ backgroundColor: badgeBackground }}
                          >
                            {badgeText}
                          </div>
                          {weekMinutes > 0 && (
                            <div className="pt-1 text-[9px] md:text-[11px] font-medium leading-none" style={{ color: "#9ca3af" }}>
                              {weekMinutes}m
                            </div>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="mt-4 md:mt-6">
            <div className="mb-3 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
              <div>
                <h3 className="text-base md:text-lg font-semibold m-0" style={{ color: "#1f2937" }}>
                  Streak Trend
                </h3>
                <p className="text-xs md:text-sm mt-1 mb-0" style={{ color: "#64748b" }}>
                  {currentStreakWeekKeys.length > MAX_STREAK_TREND_WEEKS
                    ? `Showing the last ${MAX_STREAK_TREND_WEEKS} weeks of your ${currentStreakWeekKeys.length}-week streak.`
                    : `Showing ${currentStreakWeekKeys.length || 0} week${currentStreakWeekKeys.length === 1 ? "" : "s"} in your current streak.`}
                </p>
              </div>

              {availableTrendMetrics.length > 0 && (
                <div
                  className="inline-grid rounded-lg border p-0.5"
                  style={{
                    backgroundColor: "#ffffff",
                    borderColor: "rgba(0, 107, 193, 0.14)",
                    gridTemplateColumns: `repeat(${availableTrendMetrics.length}, minmax(0, 1fr))`,
                  }}
                  aria-label="Choose streak trend metric"
                >
                  {availableTrendMetrics.map((metric) => {
                    const isSelected = metric.key === selectedTrendMetric.key;
                    return (
                      <button
                        type="button"
                        key={metric.key}
                        onClick={() => setSelectedTrendMetricKey(metric.key)}
                        className="px-2.5 py-1 rounded-md text-xs font-medium transition-colors"
                        style={{
                          color: isSelected ? "#ffffff" : "#475569",
                          backgroundColor: isSelected ? metric.accent : "transparent",
                        }}
                        aria-pressed={isSelected}
                      >
                        {metric.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <StreakTrendLineGraph
              metric={selectedTrendMetric}
              points={availableTrendMetrics.length > 0 ? streakTrendPoints : []}
              selectedWeekKey={selectedTrendWeekKey}
              onSelectWeek={setSelectedTrendWeekKey}
            />
          </div>

          <div className="mt-4 md:mt-6">
            <div className="mb-3">
              <div>
                <h3 className="text-base md:text-lg font-semibold m-0" style={{ color: "#1f2937" }}>
                  Weekly Progress
                </h3>
                <p className="text-xs md:text-sm mt-1 mb-2" style={{ color: "#64748b" }}>
                  Compare this week, last week, and your current streak average to monitor progress and maintain consistency.
                </p>
              </div>
            </div>

            {chartMetrics.length === 0 ? (
              <div
                className="rounded-2xl border px-4 py-3 text-sm"
                style={{
                  background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                  borderColor: "rgba(100, 116, 139, 0.2)",
                  color: "#64748b",
                }}
              >
                Weekly progress will appear here once you have a valid activity day for the week.
              </div>
            ) : (
              <div className="grid gap-x-10 gap-y-5 md:grid-cols-2 lg:grid-cols-3">
                {chartMetrics.map((metric) => {
                  const maxValue = Math.max(1, ...metric.bars.map((bar) => Number(bar.value || 0)));

                  return (
                    <div
                      key={metric.key}
                      className="min-w-0 rounded-2xl border px-3 py-3 md:px-4 md:py-4 shadow-sm"
                      style={{
                        backgroundColor: "#ffffff",
                        borderColor: "rgba(226, 232, 240, 0.8)",
                        boxShadow: "0 6px 18px rgba(15, 23, 42, 0.04)",
                      }}
                    >
                      <div className="mb-2">
                        <div className="flex items-center gap-2">
                          <span className="shrink-0" style={{ color: metric.accent }}>
                            {renderMetricIcon(metric.key, metric.accent)}
                          </span>
                          <h4 className="text-sm md:text-base font-semibold m-0" style={{ color: metric.accent }}>
                            {metric.title}
                          </h4>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-1 items-end min-h-[166px]">
                        {metric.bars.map((bar) => {
                          const rawHeight = (Number(bar.value || 0) / maxValue) * 100;
                          const barHeight = Number(bar.value || 0) > 0 ? Math.max(rawHeight, 14) : 0;

                          return (
                            <div key={`${metric.key}_${bar.label}`} className="flex flex-col items-center justify-end h-full">
                              <div className="mb-1 min-h-[24px] text-[10px] md:text-[11px] font-medium text-center relative" style={{ color: "#64748b" }}>
                                {bar.label === "Streak Avg" ? (
                                  <div className="inline-flex items-center justify-center gap-1 relative">
                                    <span>{bar.label}</span>
                                    <button
                                      type="button"
                                      className="inline-flex h-4 w-4 items-center justify-center rounded-full border p-0 text-[10px] font-bold leading-none"
                                      style={{
                                        color: "#475569",
                                        borderColor: "rgba(148, 163, 184, 0.35)",
                                        backgroundColor: "#ffffff",
                                      }}
                                      onClick={() =>
                                        setOpenAverageTooltipMetricKey((currentKey) =>
                                          currentKey === metric.key ? "" : metric.key
                                        )
                                      }
                                      aria-label="Show streak average info"
                                      aria-expanded={openAverageTooltipMetricKey === metric.key}
                                    >
                                      i
                                    </button>

                                    {openAverageTooltipMetricKey === metric.key && (
                                      <div
                                        className="absolute left-1/2 top-full z-20 mt-2 w-[190px] -translate-x-1/2 rounded-xl border px-3 py-2 text-[10px] md:text-[11px] font-medium text-left shadow-lg"
                                        style={{
                                          color: "#475569",
                                          backgroundColor: "#ffffff",
                                          borderColor: "rgba(148, 163, 184, 0.25)",
                                          boxShadow: "0 12px 28px rgba(15, 23, 42, 0.12)",
                                        }}
                                      >
                                        {currentStreakWeekKeys.length > 0
                                          ? `Streak average uses ${currentStreakWeekKeys.length} week${currentStreakWeekKeys.length === 1 ? "" : "s"}.`
                                          : "Streak average will appear once your streak is live."}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  bar.label
                                )}
                              </div>
                              <div
                                className={`${bar.label === "This Week" ? "text-sm font-semibold" : "text-[11px] md:text-xs font-medium"} text-center mb-1.5`}
                                style={{ color: "#1f2937" }}
                              >
                                {formatGraphMetricValue(metric.key, bar.value)}
                              </div>
                              <div
                                className="w-full max-w-[44px] md:max-w-[48px] h-[96px] md:h-[104px] flex items-end"
                              >
                                <div
                                  className="w-full rounded-md"
                                  style={{
                                    minHeight: barHeight > 0 ? "12px" : "0px",
                                    height: `${barHeight}%`,
                                    background: `linear-gradient(180deg, ${bar.color}e6 0%, ${bar.color}b8 100%)`,
                                  }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      </div>

      {isDayDetailsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            onClick={() => {
              if (!deletingEventId && !isSavingDayNote) {
                setIsDayDetailsOpen(false);
              }
            }}
            aria-label="Close day activity details"
          />
          <div
            className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl border shadow-xl p-4 md:p-5"
            style={{
              background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
              borderColor: "rgba(100, 116, 139, 0.25)",
            }}
            role="dialog"
            aria-modal="true"
            aria-label={`Activities on ${formatDayTitle(selectedDayKey)}`}
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <h3 className="text-lg font-bold m-0" style={{ color: "#1f2937" }}>
                  Activities on {formatDayTitle(selectedDayKey)}
                </h3>
                <p className="text-sm mt-1 mb-0" style={{ color: "#64748b" }}>
                  {selectedDayData?.totals?.eventsCount || 0} event
                  {selectedDayData?.totals?.eventsCount === 1 ? "" : "s"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsDayDetailsOpen(false)}
                className="w-8 h-8 rounded-full border text-sm font-semibold flex items-center justify-center"
                style={{ borderColor: "#cbd5e1", color: "#475569", background: "#ffffff" }}
                aria-label="Close"
                disabled={!!deletingEventId || isSavingDayNote}
              >
                X
              </button>
            </div>

            {canStreaks && (
              <div className="mb-4">
                <button
                  type="button"
                  onClick={openManualActivityModal}
                  className="px-3 py-2 rounded-xl border text-sm font-semibold"
                  style={{
                    borderColor: "rgba(0, 107, 193, 0.22)",
                    color: "#0b5cab",
                    background: "linear-gradient(135deg, #ffffff 0%, #eff6ff 100%)",
                  }}
                  aria-label="Add manual activity"
                >
                  Add Manual Activity
                </button>
              </div>
            )}

            {!!eventsError && (
              <div
                className="mb-3 text-sm rounded-lg px-3 py-2 border"
                style={{ background: "#fef2f2", color: "#b91c1c", borderColor: "#fecaca" }}
              >
                {eventsError}
              </div>
            )}

            <div
              className="mb-4 rounded-xl border px-3 py-3"
              style={{
                borderColor: "rgba(148, 163, 184, 0.25)",
                background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
              }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: "#64748b" }}>
                    Daily note
                  </div>
                  {!isEditingDayNote && (
                    selectedDayData?.note ? (
                      <div className="text-sm whitespace-pre-wrap break-words" style={{ color: "#334155" }}>
                        {selectedDayData.note}
                      </div>
                    ) : (
                      <div className="text-sm" style={{ color: "#64748b" }}>
                        No note for this day yet.
                      </div>
                    )
                  )}
                </div>

                {!isEditingDayNote && (
                  <button
                    type="button"
                    onClick={() => {
                      setDayNoteDraft(selectedDayData?.note || "");
                      setDayNoteError("");
                      setIsEditingDayNote(true);
                    }}
                    className="px-3 py-1.5 rounded-md border text-sm font-semibold shrink-0"
                    style={{
                      borderColor: "#bfdbfe",
                      color: "#0b5cab",
                      background: "#eff6ff",
                    }}
                    disabled={isSavingDayNote || !!deletingEventId}
                  >
                    {selectedDayData?.note ? "Edit" : "Add Note"}
                  </button>
                )}
              </div>

              {isEditingDayNote && (
                <div className="mt-3">
                  <textarea
                    value={dayNoteDraft}
                    onChange={(event) => {
                      setDayNoteDraft(event.target.value);
                      if (dayNoteError) {
                        setDayNoteError("");
                      }
                    }}
                    rows={4}
                    maxLength={1000}
                    placeholder="Add a note for this day..."
                    className="w-full rounded-xl border px-3 py-2 text-sm resize-y"
                    style={{
                      borderColor: "#cbd5e1",
                      color: "#1f2937",
                      backgroundColor: "#ffffff",
                    }}
                    disabled={isSavingDayNote}
                  />

                  <div className="mt-1 text-xs text-right" style={{ color: "#94a3b8" }}>
                    {dayNoteDraft.trim().length}/1000
                  </div>

                  {!!dayNoteError && (
                    <div
                      className="mt-2 text-sm rounded-lg px-3 py-2 border"
                      style={{ background: "#fef2f2", color: "#b91c1c", borderColor: "#fecaca" }}
                    >
                      {dayNoteError}
                    </div>
                  )}

                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={handleSaveDayNote}
                      disabled={isSavingDayNote}
                      className="px-3 py-1.5 rounded-md text-sm font-semibold text-white disabled:opacity-60"
                      style={{ background: "linear-gradient(135deg, #006bc1 0%, #0e89ef 100%)" }}
                    >
                      {isSavingDayNote ? "Saving..." : "Save"}
                    </button>

                    {selectedDayData?.note && (
                      <button
                        type="button"
                        onClick={handleDeleteDayNote}
                        disabled={isSavingDayNote}
                        className="px-3 py-1.5 rounded-md border text-sm font-semibold disabled:opacity-60"
                        style={{
                          borderColor: "#fca5a5",
                          color: "#b91c1c",
                          background: "#fef2f2",
                        }}
                      >
                        Delete
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() => {
                        setDayNoteDraft(selectedDayData?.note || "");
                        setDayNoteError("");
                        setIsEditingDayNote(false);
                      }}
                      disabled={isSavingDayNote}
                      className="px-3 py-1.5 rounded-md border text-sm font-semibold disabled:opacity-60"
                      style={{
                        borderColor: "#cbd5e1",
                        color: "#475569",
                        background: "#ffffff",
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            {eventsLoading && <div className="text-sm" style={{ color: "#64748b" }}>Loading activities...</div>}

            {!eventsLoading && selectedDayEvents.length === 0 && (
              <div className="text-sm" style={{ color: "#64748b" }}>
                {showUpcomingRacePreview
                  ? "No streak activities yet. Your activities will start filling in once the challenge begins."
                  : "No activities on this day."}
              </div>
            )}

            {!eventsLoading && selectedDayEvents.length > 0 && (
              <div className="space-y-3">
                {selectedDayEvents.map((event) => {
                  const minutes = Number(event?.metrics?.minutes || 0);
                  const distanceKm = Number(event?.metrics?.distanceKm || 0);
                  const steps = Number(event?.metrics?.steps || 0);
                  const showDistance = Number.isFinite(distanceKm) && distanceKm > 0;
                  const showMinutes = Number.isFinite(minutes) && minutes > 0;
                  const showSteps = Number.isFinite(steps) && steps > 0;

                  return (
                    <div
                      key={event.id}
                      className="rounded-xl border px-3 py-3 flex items-start justify-between gap-3"
                      style={{
                        borderColor: "rgba(148, 163, 184, 0.25)",
                        background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                      }}
                    >
                      <div>
                        <div className="text-sm font-semibold" style={{ color: "#1f2937" }}>
                          {formatEventTitle(event)}
                        </div>
                        <div className="text-xs mt-1" style={{ color: "#64748b" }}>
                          {formatEventTime(event.startTime)}
                        </div>
                        <div className="text-sm mt-1 flex flex-wrap gap-x-3 gap-y-1" style={{ color: "#334155" }}>
                          {showMinutes && <span>{Math.round(minutes)}m</span>}
                          {showDistance && <span>{distanceKm.toFixed(2)} km</span>}
                          {showSteps && <span>{Math.round(steps)} steps</span>}
                        </div>
                        {!!event?.manual?.activityName && (
                          <div className="text-xs mt-1 font-medium" style={{ color: "#334155" }}>
                            {event.manual.activityName}
                          </div>
                        )}
                        {!!event?.manual?.note && (
                          <div className="text-xs mt-1" style={{ color: "#64748b" }}>
                            {event.manual.note}
                          </div>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={() => handleDeleteEvent(event)}
                        disabled={deletingEventId === event.id}
                        className="px-3 py-1.5 rounded-md border text-sm disabled:opacity-60"
                        style={{
                          borderColor: "#fca5a5",
                          color: "#b91c1c",
                          background: "#fef2f2",
                        }}
                      >
                        {deletingEventId === event.id ? "Deleting..." : "Delete"}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {isAddActivityOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            onClick={closeManualActivityModal}
            aria-label="Close manual activity form"
          />
          <div className="relative z-10" role="dialog" aria-modal="true" aria-label="Add manual activity">
            <ManualActivityForm
              inputMaxDateString={todayKey}
              inputDefaultValueString={manualDefaultDate || todayKey}
              submissionState={isSubmittingManualActivity ? "submitting" : "idle"}
              submitError={manualSubmitError}
              clearSubmitError={() => setManualSubmitError("")}
              showActivityForm={isAddActivityOpen}
              closeActivityForm={closeManualActivityModal}
              onSubmitActivity={handleManualActivitySubmit}
              showActivityName
              requireActivityName
              allowTimeOnlyInDistanceMode
              showChallengeSelection
              challenges={manualChallenges}
              timeZone={userData?.timeZone || streakTimeZone}
              origin={MANUAL_ACTIVITY_ORIGINS.STREAKS_PAGE}
              title="Add Manual Activity"
            />
          </div>
        </div>
      )}

      {isInfoOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            onClick={() => setIsInfoOpen(false)}
            aria-label="Close streak info"
          />
          <div
            className="relative w-full max-w-md rounded-2xl border shadow-xl p-4 md:p-5"
            style={{
              background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
              borderColor: "rgba(100, 116, 139, 0.25)",
            }}
            role="dialog"
            aria-modal="true"
            aria-label="How streaks work"
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <h3 className="text-lg font-bold m-0" style={{ color: "#1f2937" }}>
                How Streaks Work
              </h3>
              <button
                type="button"
                onClick={() => setIsInfoOpen(false)}
                className="w-8 h-8 rounded-full border text-sm font-semibold flex items-center justify-center"
                style={{ borderColor: "#cbd5e1", color: "#475569", background: "#ffffff" }}
                aria-label="Close"
              >
                X
              </button>
            </div>

            <div className="text-sm space-y-4 leading-relaxed" style={{ color: "#475569" }}>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: "#0b5cab" }}>
                  Weekly Goal
                </div>
                <p className="m-0">Hit the daily activity goal on 3 different days in the same week. (M-S)</p>
              </div>

              <div>
                <div className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: "#0b5cab" }}>
                  A Day Qualifies If You Reach Any of These
                </div>
                <ul className="m-0 pl-5 space-y-1">
                  <li>More than 3 km</li>
                  <li>More than 3,000 steps</li>
                  <li>More than 20 minutes of activity</li>
                </ul>
                <p className="mt-2 mb-0">You can hit that with one activity or multiple activities on the same day.</p>
              </div>



              <div>
                <div className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: "#0b5cab" }}>
                  Good To Know
                </div>
                <p className="m-0">Use Add Manual Activity to log an activity from this page.</p>
                <p className="mt-2 mb-0">Distance and step entries can also be added to any eligible active challenges you select.</p>
                <p className="mt-2 mb-0">Tap any day to view all logged activities for that date.</p>
                <p className="mt-2 mb-0">Deleting a newly linked activity also updates its selected challenge totals.</p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setIsInfoOpen(false)}
              className="mt-4 w-full px-4 py-2 rounded-xl text-white font-semibold"
              style={{ background: "linear-gradient(135deg, #006bc1 0%, #0e89ef 100%)" }}
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
